import type { ItemDto, ItemListResponse } from '@project-os/contracts';
import type { CreateItemInput, QuickAddItemInput, UpdateItemInput } from '@project-os/validation';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './client';
import { projectKeys } from './projects';

/** Фильтры списка в том виде, в каком их держит страница «Элементы». */
export interface ItemQuery {
  type?: string[];
  status?: string[];
  workstreamId?: string[];
  ownerId?: string[];
  priority?: string[];
  isNextAction?: boolean;
  openOnly?: boolean;
  search?: string;
  includeArchived?: boolean;
  sort?: string;
  direction?: 'asc' | 'desc';
  /** Доска и календарь показывают всё сразу, поэтому просят больше строк. */
  limit?: number;
}

export const itemKeys = {
  all: (projectId: string) => ['projects', projectId, 'items'] as const,
  list: (projectId: string, query: ItemQuery) =>
    ['projects', projectId, 'items', 'list', query] as const,
  detail: (projectId: string, itemId: string) =>
    ['projects', projectId, 'items', 'detail', itemId] as const,
};

function buildSearchParams(query: ItemQuery, cursor?: string): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  }
  if (cursor) params.set('cursor', cursor);

  const search = params.toString();
  return search ? `?${search}` : '';
}

/** Список элементов с подгрузкой по курсору. */
export function useItems(projectId: string, query: ItemQuery) {
  return useInfiniteQuery({
    queryKey: itemKeys.list(projectId, query),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiRequest<ItemListResponse>(
        `/projects/${projectId}/items${buildSearchParams(query, pageParam)}`,
        { signal },
      ),
    getNextPageParam: (lastPage) => lastPage.page.nextCursor ?? undefined,
    enabled: Boolean(projectId),
  });
}

export function useItem(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemKeys.detail(projectId, itemId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ItemDto>(`/projects/${projectId}/items/${itemId}`, { signal }),
    enabled: Boolean(projectId && itemId),
  });
}

/**
 * После любого изменения список и карточки проекта перезапрашиваются:
 * счётчики блокеров и следующих действий живут на нескольких экранах.
 */
/**
 * Сброс кэша после правки элемента.
 *
 * Обновляется всё, что относится к проекту, а не только список элементов:
 * от элементов зависят обзор, этапы, расписание и уведомления. Перечислять
 * их по одному значило бы забывать про новые — так и вышло с расписанием:
 * перенос задачи на другой день сохранялся, но календарь показывал старую
 * дату до перезагрузки страницы.
 *
 * Ключи проекта устроены как ['projects', projectId, ...], поэтому один
 * префикс закрывает их все. Карточка проекта лежит в другой ветке
 * (['projects', 'detail', id]) и сбрасывается отдельно.
 */
function useItemInvalidation(projectId: string) {
  const queryClient = useQueryClient();
  return (item?: ItemDto) => {
    if (item) queryClient.setQueryData(itemKeys.detail(projectId, item.id), item);
    void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
  };
}

export function useQuickAddItem(projectId: string) {
  const invalidate = useItemInvalidation(projectId);
  return useMutation({
    mutationFn: (input: QuickAddItemInput) =>
      apiRequest<ItemDto>(`/projects/${projectId}/items/quick`, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useCreateItem(projectId: string) {
  const invalidate = useItemInvalidation(projectId);
  return useMutation({
    mutationFn: (input: CreateItemInput) =>
      apiRequest<ItemDto>(`/projects/${projectId}/items`, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateItem(projectId: string) {
  const invalidate = useItemInvalidation(projectId);
  return useMutation({
    mutationFn: (input: UpdateItemInput & { itemId: string }) => {
      const { itemId, ...body } = input;
      return apiRequest<ItemDto>(`/projects/${projectId}/items/${itemId}`, {
        method: 'PATCH',
        body,
      });
    },
    onSuccess: invalidate,
  });
}

export function useArchiveItem(projectId: string) {
  const invalidate = useItemInvalidation(projectId);
  return useMutation({
    mutationFn: (itemId: string) =>
      apiRequest<ItemDto>(`/projects/${projectId}/items/${itemId}/archive`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useRestoreItem(projectId: string) {
  const invalidate = useItemInvalidation(projectId);
  return useMutation({
    mutationFn: (itemId: string) =>
      apiRequest<ItemDto>(`/projects/${projectId}/items/${itemId}/restore`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
